import firebase from '../../services/firebase';
import ViewData from './views.js';
import { getAuthRoles } from '@mikmak/platform-spa-wrapper';
import { getUsers } from '@mikmak/platform-spa-wrapper';
import { getAllRetailersFromLocationApi } from '../../services/retailLocationService';

// Main/Global state for the platform.
const state = {

  // Brands
  brands: undefined,
  inactiveBrands: undefined,
  amazonBrands: undefined,
  mappedAmazonBrands: undefined,
  unMappedAmazonBrands: undefined,
  currentUser: undefined,
  brandManagers: [],
  authRoles: [],
  countryCurrencies: {},
  countryCurrenciesList: [],

  // Retailer Carts
  retailerCarts: undefined,
  inactiveCarts: undefined,
  cartList: [],
  discoverCarts: undefined,
  retailerCartsFromLocationApi: undefined,

  // Loading Overlay visiblity and message
  loadingOverlayVisible: undefined,
  loadingOverlayMessage: '',

  // Snackbar Messages
  snackbarVisible: undefined,
  snackbarMessage: undefined,
  snackbarState: 'success',

  // Mobile menu
  mobileMenuVisible: false,

  // View data
  currentView: undefined,
  defaultView: {
    name: 'Attach',
    route: '/',
    slug: 'attach',
  },
  activeView: undefined,
  defaultSelfServeView: '/dashboard',
  views: ViewData.views,
  viewSubnav: ViewData.subnav,

  // Ad Platform Connect data
  selectedPlayerId: undefined,

  // Feature Flags
  featureFlags: [],
  showProductCTAs: false,
  shouldShowDataManagement: false,

  // NavBar
  marginBehindNavBar: 202,
};

const mutations = {
  setCartList(state, carts) {
    state.cartList = carts;
  },
  setBrands(state, brands) {
    state.brands = brands;
  },
  setAllAmazonBrands(state, brands) {
    state.amazonBrands = brands;
  },
  setInactiveBrands(state, brands) {
    state.inactiveBrands = brands;
  },
  setMappedAmazonBrands(state, brands) {
    state.mappedAmazonBrands = brands;
  },
  setUnMappedAmazonBrands(state, brands) {
    state.unMappedAmazonBrands = brands;
  },
  setBrandManagers(state, brandManagers) {
    state.brandManagers = brandManagers;
  },
  setAuthRoles(state, authRoles) {
    state.authRoles = authRoles;
  },
  setCountryCurrencies(state, countryCurrencies) {
    state.countryCurrencies = countryCurrencies;
    state.countryCurrenciesList = Object.values(countryCurrencies)
      .map(({ name, isoAlpha3, currency }) => ({
        key: `${name} (${currency.symbol || currency.code})`,
        value: isoAlpha3,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  },
  toggleMobileMenu(state) {
    state.mobileMenuVisible = !state.mobileMenuVisible;
  },
  hideMobileMenu(state) {
    state.mobileMenuVisible = false;
  },
  updateCurrentUser(state, user) {
    state.currentUser = user;
  },
  showSnackbar(state, message) {
    state.snackbarVisible = true;
    state.snackbarMessage = message;
  },
  showLoadingMessage(state, message) {
    state.loadingOverlayVisible = true;
    state.loadingOverlayMessage = message;
  },
  hideLoadingMessage() {
    state.loadingOverlayVisible = false;
  },
  hideSnackbar() {
    state.snackbarVisible = false;
  },
  setSnackbarState(state, sbState) {
    sbState = sbState || 'success';
    state.snackbarState = sbState;
  },
  updateCurrentView(state, view) {
    state.views.map((viewObj) => {
      if (viewObj.name == view) state.currentView = viewObj;
    });
  },
  setRetailerCarts(state, carts) {
    state.retailerCarts = carts;
  },
  setInactveRetailerCarts(state, carts) {
    state.inactiveCarts = carts;
  },
  updateActiveView(state, view) {
    state.activeView = view;
  },
  setDiscoverCarts(state, carts) {
    state.discoverCarts = carts;
  },
  setRetailersCartsFromLocationAPI(state, carts) {
    state.retailerCartsFromLocationApi = carts;
  },
  updateBrands(state, brand) {
    const brands = Object.assign({}, state.brands);
    const brandId = brand.brandId;

    if (Object.keys(brands).length) {
      brands[brandId] = {
        ...state.brands[brandId],
        ...brand,
      };
      state.brands = brands;
    }
  },
  removeUnMappedAmazonBrand(state, unmappedAmazonBrand) {
    const mappedAmazonBrands = Object.assign({}, state.mappedAmazonBrands);
    const brandId = unmappedAmazonBrand.id_country;

    if (!unmappedAmazonBrand.isMapped) delete mappedAmazonBrands[brandId];

    state.mappedAmazonBrands = mappedAmazonBrands;
  },
  removeMappedAmazonBrand(state, mappedAmazonBrand) {
    const unMappedAmazonBrands = Object.assign({}, state.unMappedAmazonBrands);
    const brandId = mappedAmazonBrand.id_country;

    if (mappedAmazonBrand.isMapped) delete unMappedAmazonBrands[brandId];

    state.unMappedAmazonBrands = unMappedAmazonBrands;
  },
  setSelectedPlayerId(state, playerId) {
    state.selectedPlayerId = playerId;
  },
  updateFeatureFlags(state, flags) {
    const activeFlags = flags.filter(flag => flag.enabled);

    state.featureFlags = activeFlags;

    state.showProductCTAs = activeFlags.some(flag => (
      flag.name === 'release-product-view-level-ctas'
    ));

    state.shouldShowDataManagement = activeFlags.some(flag => (
      flag.name === 'release-data-management-view'
    ));
  },
  setMarginBehindNavBar(state, marginBehindNavBar) {
    state.marginBehindNavBar = marginBehindNavBar;
  },
};

const actions = {
  setAllBrandData: async ({ commit }, token) => {
    const brands = await firebase.getAllActiveBrands(token);

    commit('setBrands', brands);
  },
  setAllInactiveBrandData: async ({ commit }, token) => {
    const brands = await firebase.getAllInactiveBrands(token);

    commit('setInactiveBrands', brands);
  },
  setAllAmazonBrands: async ({ commit }, token) => {
    const brands = await firebase.getAllAmazonBrands(token);

    commit('setAllAmazonBrands', brands);
  },
  setAllMappedAmazonBrands: async ({ commit }, token) => {
    const brands = await firebase.getMappedAmazonBrands(token);

    commit('setMappedAmazonBrands', brands);
  },
  setAllUnMappedAmazonBrands: async ({ commit }, token) => {
    const brands = await firebase.getUnMappedAmazonBrands(token);

    commit('setUnMappedAmazonBrands', brands);
  },
  setAllActiveCartData: async ({ commit }, token) => {
    const carts = await firebase.getAllActiveCarts(token);

    commit('setRetailerCarts', carts);
  },
  setAllInactiveCartData: async ({ commit }, token) => {
    const carts = await firebase.getAllInactiveCarts(token);

    commit('setInactveRetailerCarts', carts);
  },
  setDiscoverCartData: async ({ commit }, token) => {
    const carts = await firebase.getDiscoverCarts(token);

    commit('setDiscoverCarts', carts);
  },
  setRetailerCartData: async ({ commit }) => {
    const cartsResponse = await getAllRetailersFromLocationApi();

    commit('setRetailersCartsFromLocationAPI', cartsResponse.retailers);
  },
  setAllBrandManagers: async ({ commit }, token) => {
    const brandManagers = await getUsers(
      {
        blocked: false,
        brand_manager: true,
        fields: 'email',
        per_page: 100,
        sort: 'email:1',
      },
      token,
    );

    commit('setBrandManagers', brandManagers);
  },
  setAllAuthRolesData: async ({ commit }, token) => {
    const authRoles = await getAuthRoles(token);

    commit('setAuthRoles', authRoles);
  },
  setCountryCurrenciesData: async ({ commit }, token) => {
    const countryCurrencies = await firebase.getCountryCurrencies(token);

    commit('setCountryCurrencies', countryCurrencies);
  },
  setFeatureFlags: ({ commit }, flags) => {
    commit('updateFeatureFlags', flags);
  },
};

const module = {
  state,
  mutations,
  actions,
};

export default module;
